import React, { useState, useMemo } from 'react'
import { CSSTransition } from 'react-transition-group'
import analytics from 'global/AnalyticsData'
import { processPollVote, getPollQuestionAnswerCounts } from 'services/poll'
import PollItem from './components/PollItem'
import ShareIcons from 'components/ShareIcons'
import PollAnalyticsContext from './components/PollAnalyticsContext'

import 'steps/shared/quiz-trivia/editorial-trivia-quiz.scss'
import 'steps/personality-quiz/quiz3.scss'
import 'steps/poll/PollStep3.scss'

const PollStep = ({
  brk,
  step,
  onEngage = function () {},
  onAwardPoints = function () {},
  onChangeBackground = function () {},
  skipButton,
  nextStepButton,
  isPuck = false,
  /** Optional: render slot content per question. Receives (question, index) and returns ReactNode. */
  renderQuestionSlot,
  mockVoting = false,
}) => {
  const pollObject = step.poll

  const [position, setPosition] = useState(0)

  const questions = useMemo(() => {
    return pollObject.questions.map((item, i) => {
      item.index = i
      return item
    })
  }, [pollObject?.questions])

  const handleShareClick = ({ platform }) => {
    analytics.stepEvent('step_interaction_poll_share_option', step, {
      result_id: platform,
      result: platform,
    })
  }

  const socialShareComponent = step.socialShareOn && (
    <ShareIcons
      facebookShare={step.facebookShare}
      twitterShare={step.twitterShare}
      pinterestShare={step.pinterestShare}
      emailShare={step.emailShare}
      emailConfig={{
        subject: step.shareEmailSubject,
        body: step.shareEmailBody,
      }}
      copyShare={step.copyShare}
      whatsappShare={step.whatsappShare}
      shareUrl={brk ? brk.shareUrl : ''}
      onShareClick={handleShareClick}
    />
  )

  const constructPollVoteAnswerArray = function (questionId, answerId, arr) {
    let questionAnswers = questions.filter(function (a) {
      return a.id === questionId
    })
    // shallow clone: we only attach pollAnswerCount/userPickedThis. A JSON
    // deep clone breaks puck answers (they carry rendered React nodes, which
    // are circular structures)
    let newAnswerArray = questionAnswers[0].answers.map(a => ({ ...a }))
    arr.forEach(function (ans) {
      let ansArrayItemIndex = newAnswerArray.findIndex(
        answer => answer.id === ans.answerId,
      )
      if (newAnswerArray[ansArrayItemIndex]) {
        newAnswerArray[ansArrayItemIndex].pollAnswerCount = ans.count
      }
    })
    let userAnswerIndex = newAnswerArray.findIndex(
      answer => answer.id === answerId,
    )
    if (newAnswerArray[userAnswerIndex]) {
      newAnswerArray[userAnswerIndex].userPickedThis = true
    }
    return newAnswerArray
  }

  const attemptToProcessVote = async (pollId, questionId, answerId) => {
    // TODO: remove when backend supports puck polls
    if (mockVoting) {
      const question = questions.find(q => q.id === questionId)
      if (!question) return undefined
      return question.answers.map(a => ({
        ...a,
        pollAnswerCount: Math.floor(Math.random() * 100) + 1,
        userPickedThis: a.id === answerId,
      }))
    }

    try {
      let pollVoteRes = await processPollVote(pollId, questionId, answerId)
      let pollVoteData = constructPollVoteAnswerArray(
        questionId,
        answerId,
        pollVoteRes.detail,
      )
      return pollVoteData
    } catch (e) {
      console.log('user already voted')
      try {
        let pollVoteRes = await getPollQuestionAnswerCounts(
          step.poll.id,
          questionId,
          answerId,
        )
        let pollVoteData = constructPollVoteAnswerArray(
          questionId,
          answerId,
          pollVoteRes.detail,
        )
        return pollVoteData
      } catch (e) {
        console.log('error submitting poll data ' + e)
      }
    }
  }

  //after user selects poll answer, send that info to the server (and you'll get back server info for that Q)
  const handleAnswerClick = async ({ questionId, answerId }) => {
    try {
      console.log(questionId, answerId, questions)
      const question = questions.find(item => {
        return item.id === questionId
      })

      const position = question.index

      const answer = question.answers.find(item => {
        return item.id === answerId
      })

      console.log('step event: step_interaction_poll_answer_selected')
      analytics.stepEvent('step_interaction_poll_answer_selected', step, {
        context_id: questionId,
        context: question.text,
        result_id: answerId,
        result: answer.text,
        context_total: questions.length,
        context_position: position,
      })

      let theResults = await attemptToProcessVote(
        pollObject.id,
        questionId,
        answerId,
      )

      return theResults
    } catch (e) {
      console.log(`error submitting poll data: ${e}`)
    }
  }

  const handleAnswerLinkClick = ({
    questionId,
    answerId,
    productId,
    link,
    linkTitle,
  }) => {
    analytics.stepEvent('step_interaction_referral_select_click', step, {
      context_id: questionId,
      result_id: answerId,
      product_id: productId,
      link,
      linkTitle,
    })
  }

  const handleAnswerDropdownClick = ({
    questionId,
    answerId,
    productId,
    dropdownTitle,
  }) => {
    analytics.stepEvent('step_interaction_referral_select', step, {
      context_id: questionId,
      result_id: answerId,
      product_id: productId,
      dropdownTitle,
    })
  }

  const handleNextQuestion = () => {
    console.log('User clicks to next question')
    setPosition(position + 1)
  }

  const animationDuration = 400

  const allQuestionsAnsweredHandler = () => {
    onEngage()
    onAwardPoints()
  }

  // we all hate this, but it's the only way to get style data from beaker to be applied to desktop-only
  const inlineStyle = `
        .step-${step.id} {
            ${
              step.backgroundColor
                ? `background-color: ${step.backgroundColor};`
                : ``
            }
        }

        @media (min-width: 900px) {
            .step-${step.id} {
            ${
              step.backgroundImage
                ? `background: url(${process.env.SITE_ASSET_URL}/${step.backgroundImage.id});`
                : ``
            }
            }
        }
        .poll-step.poll-step-3 .poll-question-list li .custom-btn-label input[type="checkbox"]:checked + .custom-checkbox:before,
        .poll-step.poll-step-3 .poll-question-list li .custom-btn-label input[type="radio"]:checked + .custom-checkbox:before {
            background-color: var(--poll-answer-check-background)
        }
    `

  return (
    <PollAnalyticsContext.Provider
      value={{ onAnswerLinkClick: handleAnswerLinkClick, onAnswerDropdownClick: handleAnswerDropdownClick }}
    >
      {questions && (
        <section className={`poll-step poll-step-3 quiz quiz-integrated`}>
          <style
            dangerouslySetInnerHTML={{
              __html: inlineStyle,
            }}
          />
          {questions.map((question, i) => {
            return (
              <CSSTransition
                in={position === i}
                timeout={animationDuration}
                key={`db-react-slide-${i}`}
                classNames="db-react-slide"
                unmountOnExit={false}
              >
                <div className={`db-react-slide db-react-slide-${i}`}>
                  {position === i && (
                    <PollItem
                      key={i}
                      question={question}
                      totalQuestions={questions.length}
                      onAnswerClick={handleAnswerClick}
                      onAnswerLinkClick={handleAnswerLinkClick}
                      onAnswerDropdownClick={handleAnswerDropdownClick}
                      backgroundImage={step.backgroundImage}
                      backgroundColor={step.backgroundColor}
                      handleNextQuestion={handleNextQuestion}
                      skipButton={skipButton}
                      nextStepButton={nextStepButton}
                      onAllQuestionsAnswered={allQuestionsAnsweredHandler}
                      socialShareComponent={socialShareComponent}
                      sponsoredType={brk?.sponsoredType}
                      poll={pollObject}
                      isPuck={isPuck}
                      puckCardInlineStyle={step.puckCardInlineStyle}
                      puckCardOverlayStyle={step.puckCardOverlayStyle}
                      puckBeforeCardContent={step.puckBeforeCardContent}
                      puckAfterCardContent={step.puckAfterCardContent}
                      puckAfterVoteContent={step.puckAfterVoteContent}
                      puckProgressBarContent={step.puckProgressBarContent}
                      puckProgressBarProps={step.puckProgressBarProps}
                      slotContent={
                        renderQuestionSlot
                          ? renderQuestionSlot(question, i)
                          : undefined
                      }
                    />
                  )}
                </div>
              </CSSTransition>
            )
          })}
        </section>
      )}
    </PollAnalyticsContext.Provider>
  )
}

export default PollStep
